// The achievement catalogue (v2, designed with Mathias 2026-10-02, see
// docs/design/achievements/PROPOSAL.md) and the one function that folds a
// finished match into a player's progress. Lives in core so the edge-server
// (which decides unlocks and pays their eggs) and the client (which shows them)
// read the exact same thresholds and rewards.
//
// Most achievements have levels (I to V); each level pays a few Golden Eggs,
// once. The amounts are deliberately small (a quarter of the first draft):
// more achievements will come, and every one of them adds to the free total.
// Size a new one against TOTAL_EGGS, not on its own.
//
// Online matches count: Ranked, a Play Now set (its seven rounds) and a Royale
// (each player's run, folded in when they go out or it ends). Never a game room
// or a local game, which could be farmed with friends. Progress is for keeps:
// a level stays reached even if a rating falls.
// (Plain ES5: the core tests run in an old browser engine.)

// Rating floors of the tiers Climber names (the client's shared/rating/tiers.ts).
var TIER_FLOORS = { platinum: 1300, diamond: 1600, master: 1900, wormlord: 2200 };
// A top 3 is a podium only in a field this big (a top 3 of four is no feat).
export var PODIUM_MIN_FIELD = 6;
// Giant Slayer: a win against this many opponents or more.
export var GIANT_MIN_OPPONENTS = 9;
// Untouchable: this average placement (share of the field beaten, %) over at
// least this many casual matches.
export var UNTOUCHABLE_PCT = 75;
export var UNTOUCHABLE_GAMES = 25;
// Rocket Worm / Gravity: a single match's rating move.
var ROCKET_GAIN = 100;
var GRAVITY_DROP = 60;

// The Royale's marks: the top 23 (past the first cut), 12, 5 and the final's 2.
export var ROYALE_MARKS = [23, 12, 5, 2];

// How many of the marks a Royale placing reached (0 to 4).
export function royaleReach(place) {
    var n = 0;
    ROYALE_MARKS.forEach(function (m) { if (place > 0 && place <= m) {n++;} });
    return n;
}

var STD = [2, 3, 5, 10, 20]; // eggs per level, the standard ladder

function plural(n, one, many) { return n === 1 ? one : many; }
function fmt(n) { return String(n).replace(/\B(?=(\d{3})+(?!\d))/g, ","); }

// Each achievement: `metric` is the progress field it reads; `levels` the
// thresholds (reached when progress >= at) with their egg reward; `goal(at)`
// says what a level asks, in the player's words.
export var ACHIEVEMENTS = [
    { id: "certified-worm", group: "Playing", icon: "🐛", name: "Certified Worm", metric: "games",
        levels: [1, 10, 50, 200, 1000], eggs: STD,
        goal: function (at) { return at === 1 ? "Play your first online match" : "Play " + fmt(at) + " online matches"; } },
    { id: "food-chain", group: "Playing", icon: "🥇", name: "Top of the Food Chain", metric: "wins",
        levels: [1, 10, 50, 150, 500], eggs: STD,
        goal: function (at) { return at === 1 ? "Win an online match" : "Win " + fmt(at) + " online matches"; } },
    { id: "podium-regular", group: "Playing", icon: "🍾", name: "Podium Regular", metric: "podiums",
        levels: [5, 25, 100, 300, 1000], eggs: STD,
        goal: function (at) { return "Finish top 3 in a match of " + PODIUM_MIN_FIELD + "+ worms, " + fmt(at) + " times"; } },
    { id: "daily-special", group: "Playing", icon: "📅", name: "Daily Special", metric: "bestStreak",
        levels: [3, 7, 14, 30], eggs: [3, 5, 10, 20],
        goal: function (at) { return "Play on " + at + " days in a row"; } },
    { id: "the-grind", group: "Playing", icon: "🔁", name: "The Grind", metric: "bestDay",
        levels: [5, 15, 30], eggs: [2, 3, 5],
        goal: function (at) { return "Play " + at + " matches in one day"; } },

    { id: "climber", group: "Rank", icon: "👑", name: "Climber", metric: "peak",
        levels: [TIER_FLOORS.platinum, TIER_FLOORS.diamond, TIER_FLOORS.master, TIER_FLOORS.wormlord], eggs: [5, 10, 20, 38],
        labels: ["Platinum", "Diamond", "Master", "Wormlord"],
        goal: function (at, i) { return "Reach " + ["Platinum", "Diamond", "Master", "Wormlord"][i] + " on any ladder"; } },
    { id: "ranked-regular", group: "Rank", icon: "🎯", name: "Ranked Regular", metric: "rankedGames",
        levels: [5, 25, 100, 300], eggs: [3, 5, 10, 20],
        goal: function (at) { return at === 5 ? "Finish your ranked placements" : "Play " + fmt(at) + " ranked matches"; } },
    { id: "duelist", group: "Rank", icon: "⚔️", name: "Duelist", metric: "duelWins",
        levels: [1, 10, 50, 150, 500], eggs: STD,
        goal: function (at) { return at === 1 ? "Win a ranked duel" : "Win " + fmt(at) + " ranked duels"; } },
    { id: "untouchable", group: "Rank", icon: "🛡️", name: "Untouchable", metric: "untouchable",
        levels: [1], eggs: [10],
        goal: function () { return "Hold " + UNTOUCHABLE_PCT + "%+ average placement over " + UNTOUCHABLE_GAMES + "+ casual matches"; } },

    { id: "giant-slayer", group: "Big matches", icon: "🗡️", name: "Giant Slayer", metric: "bigWins",
        levels: [1, 5, 25], eggs: [4, 8, 15],
        goal: function (at) { return "Win " + (at === 1 ? "a match" : at + " matches") + " against " + GIANT_MIN_OPPONENTS + "+ opponents"; } },

    { id: "royale-regular", group: "Royale", icon: "🎟️", name: "Royale Regular", metric: "royales",
        levels: [1, 5, 25, 100], eggs: [2, 3, 5, 10],
        goal: function (at) { return at === 1 ? "Play your first Royale" : "Play " + fmt(at) + " Royales"; } },
    { id: "royale-runner", group: "Royale", icon: "🏃", name: "Royale Runner", metric: "royaleReach",
        levels: [1, 2, 3, 4], eggs: [2, 3, 5, 10],
        goal: function (at) { return at === 4 ? "Reach a Royale final" : "Make the last " + ROYALE_MARKS[at - 1] + " of a Royale"; } },
    { id: "rocket-worm", group: "Moments", icon: "🚀", name: "Rocket Worm", metric: "biggestClimb",
        levels: [ROCKET_GAIN], eggs: [7],
        goal: function () { return "Gain " + ROCKET_GAIN + "+ rating in one match"; } },
    { id: "gravity", group: "Moments", icon: "🪨", name: "Gravity", metric: "biggestDrop",
        levels: [GRAVITY_DROP], eggs: [3],
        goal: function () { return "Lose " + GRAVITY_DROP + "+ rating in one match. What goes up..."; } },

    { id: "round-robin", group: "Skill and style", icon: "🏁", name: "Round Robin", metric: "roundsWon",
        levels: [10, 50, 250, 1000, 5000], eggs: STD,
        goal: function (at) { return "Win " + fmt(at) + " rounds"; } },
    { id: "needle-threader", group: "Skill and style", icon: "🧵", name: "Needle Threader", metric: "gapsThreaded",
        levels: [10, 50, 200, 1000, 5000], eggs: STD,
        goal: function (at) { return "Thread " + fmt(at) + " gaps"; } },
    { id: "power-hungry", group: "Skill and style", icon: "⚡", name: "Power Hungry", metric: "powerUps",
        levels: [25, 100, 500, 2000, 10000], eggs: STD,
        goal: function (at) { return "Grab " + fmt(at) + " power-ups"; } },
    { id: "wall-builder", group: "Skill and style", icon: "🧱", name: "Wall Builder", metric: "crashesCaused",
        levels: [10, 50, 250, 1000, 5000], eggs: STD,
        goal: function (at) { return "Make " + fmt(at) + " worms crash into your trail"; } },
];

export var LEVEL_NUMERALS = ["I", "II", "III", "IV", "V"];

export var TOTAL_EGGS = ACHIEVEMENTS.reduce(function (s, a) { return s + a.eggs.reduce(function (x, y) { return x + y; }, 0); }, 0);

export function achievementById(id) {
    for (var i = 0; i < ACHIEVEMENTS.length; i++) {if (ACHIEVEMENTS[i].id === id) {return ACHIEVEMENTS[i];}}
    return null;
}

// A level's name: "Certified Worm III", or the plain name for a one-level one.
export function levelName(a, level) {
    return a.levels.length > 1 ? a.name + " " + LEVEL_NUMERALS[level - 1] : a.name;
}

export function emptyProgress() {
    return {
        games: 0, wins: 0, podiums: 0, rankedGames: 0, duelWins: 0, bigWins: 0,
        peak: 0, biggestClimb: 0, biggestDrop: 0,
        roundsWon: 0, gapsThreaded: 0, powerUps: 0, crashesCaused: 0,
        untouchable: 0,
        royales: 0, royaleReach: 0,
        lastDay: null, streak: 0, bestStreak: 0, dayCount: 0, bestDay: 0,
    };
}

function copy(p) {
    var out = emptyProgress();
    for (var k in out) {if (p && p[k] !== undefined && p[k] !== null) {out[k] = p[k];}}
    return out;
}

// Whole days from one "YYYY-MM-DD" to the next (calendar days: noon UTC to
// noon UTC, so no clock change can make a day count twice).
function dayGap(a, b) {
    function at(s) { return Date.UTC(+s.slice(0, 4), +s.slice(5, 7) - 1, +s.slice(8, 10), 12); }
    return Math.round((at(b) - at(a)) / 86400000);
}

// Fold one rated match into `progress` (returns a new object). `m`:
//   ranked       "duel" | "ffa" | null (casual)
//   placement    1 = won;  fieldSize: worms in the match
//   ratingBefore / ratingAfter   display ratings around the match
//   placing      a ranked placement match before the last (its rating is
//                hidden: no climb, drop or peak counts from it)
//   hatching     the last placement match: the rank appears, so its rating
//                counts toward the peak, but the move into it was never seen
//   day          "YYYY-MM-DD", the player's own calendar day
//   stats        this match's fun stats for the player (core aggregateMatchStats
//                byPlayer entry: roundsWon, gapsThreaded, powerUps {self, others,
//                all}, crashesCaused), or null
//   royale       true for a Royale run (placement and fieldSize are its own)
//   casual       the casual ladder after the match: { games, placeScore }
//                (placeScore / games = average share of the field beaten), or null
export function foldMatch(progress, m) {
    var p = copy(progress);
    p.games += 1;
    var won = m.placement === 1;
    if (won) {p.wins += 1;}
    if (m.placement <= 3 && m.fieldSize >= PODIUM_MIN_FIELD) {p.podiums += 1;}
    if (won && m.fieldSize - 1 >= GIANT_MIN_OPPONENTS) {p.bigWins += 1;}
    if (m.ranked) {
        p.rankedGames += 1;
        if (m.ranked === "duel" && won) {p.duelWins += 1;}
    }
    if (!m.placing && typeof m.ratingAfter === "number") {
        if (m.ratingAfter > p.peak) {p.peak = m.ratingAfter;}
        if (!m.hatching && typeof m.ratingBefore === "number") {
            var delta = m.ratingAfter - m.ratingBefore;
            if (delta > p.biggestClimb) {p.biggestClimb = delta;}
            if (-delta > p.biggestDrop) {p.biggestDrop = -delta;}
        }
    }
    if (m.royale) {
        p.royales += 1;
        var reach = royaleReach(m.placement);
        if (reach > p.royaleReach) {p.royaleReach = reach;}
    }
    var s = m.stats;
    if (s) {
        p.roundsWon += s.roundsWon || 0;
        p.gapsThreaded += s.gapsThreaded || 0;
        p.crashesCaused += s.crashesCaused || 0;
        var pu = s.powerUps || {};
        p.powerUps += (pu.self || 0) + (pu.others || 0) + (pu.all || 0);
    }
    var c = m.casual;
    if (c && c.games >= UNTOUCHABLE_GAMES && 100 * (c.placeScore || 0) / c.games >= UNTOUCHABLE_PCT) {p.untouchable = 1;}
    if (m.day) {
        if (p.lastDay === m.day) {
            p.dayCount += 1;
        } else {
            p.streak = p.lastDay && dayGap(p.lastDay, m.day) === 1 ? p.streak + 1 : 1;
            p.dayCount = 1;
            p.lastDay = m.day;
        }
        if (p.streak > p.bestStreak) {p.bestStreak = p.streak;}
        if (p.dayCount > p.bestDay) {p.bestDay = p.dayCount;}
    }
    return p;
}

// How many levels of `a` the progress has reached (0 = none).
export function levelOf(a, progress) {
    var v = (progress && progress[a.metric]) || 0;
    var n = 0;
    while (n < a.levels.length && v >= a.levels[n]) {n++;}
    return n;
}

// { achievementId: levels reached } for every achievement.
export function levelsFor(progress) {
    var out = {};
    ACHIEVEMENTS.forEach(function (a) { out[a.id] = levelOf(a, progress); });
    return out;
}

// The levels reached in `after` but not in `before` (both levelsFor maps), one
// entry per level (a jump of two levels pays both): [{ id, level, eggs }].
export function newLevels(before, after) {
    var out = [];
    ACHIEVEMENTS.forEach(function (a) {
        var from = (before && before[a.id]) || 0, to = (after && after[a.id]) || 0;
        for (var l = from + 1; l <= to; l++) {out.push({ id: a.id, level: l, eggs: a.eggs[l - 1] });}
    });
    return out;
}

// What a match moved, for the little cards after it: every achievement whose
// progress grew, as { id, from, to, level, levelBefore, start, next } (`start`
// the threshold of the level reached, 0 before the first; `next` the next
// level's, null when it was the last). The levels reached come first, then the
// ones nearest their next level; at most `limit` (5).
export function progressMoves(before, after, limit) {
    var max = typeof limit === "number" ? limit : 5;
    var out = [];
    ACHIEVEMENTS.forEach(function (a) {
        var from = Number((before && before[a.metric]) || 0), to = Number((after && after[a.metric]) || 0);
        if (!(to > from)) {return;}
        var lb = levelOf(a, before), la = levelOf(a, after);
        out.push({ id: a.id, from: from, to: to, level: la, levelBefore: lb,
            start: la > 0 ? a.levels[la - 1] : 0, next: la < a.levels.length ? a.levels[la] : null });
    });
    var near = function (m) { return m.next === null ? 1 : (m.to - m.start) / Math.max(1, m.next - m.start); };
    out.sort(function (x, y) { return (y.level - y.levelBefore) - (x.level - x.levelBefore) || near(y) - near(x); });
    return out.slice(0, max);
}

// A date's calendar day in an IANA time zone ("Europe/Stockholm"), as
// "YYYY-MM-DD"; UTC when the zone is missing or unknown.
export function dayIn(date, timeZone) {
    try {
        if (timeZone) {
            var parts = new Intl.DateTimeFormat("en-CA", { timeZone: timeZone, year: "numeric", month: "2-digit", day: "2-digit" }).formatToParts(date);
            var y = "", mo = "", d = "";
            parts.forEach(function (pt) { if (pt.type === "year") {y = pt.value;} else if (pt.type === "month") {mo = pt.value;} else if (pt.type === "day") {d = pt.value;} });
            if (y && mo && d) {return y + "-" + mo + "-" + d;}
        }
    } catch (e) { /* unknown zone: UTC below */ }
    return date.toISOString().slice(0, 10);
}
